// src/layout/builders/ERP/ProjectInchargeTab.jsx
import React, { useState, useEffect, useCallback } from "react";
import { load } from "@cashfreepayments/cashfree-js";
import useAuthStore from "../../../store/useAuthStore";
import { axiosInstancev1 } from "../../../lib/axiosInstance";
import toast from "react-hot-toast";
import { 
  ShieldCheck, 
  Trash2, 
  Edit2, 
  X, 
  Lock, 
  Unlock, 
  CreditCard,
  UserPlus,
  Users,
  Check
} from "lucide-react";

const FREE_LIMIT = 5;

const PERSON_PACKAGES = [
  { user_count: 6, persons: 1, label: "1 Extra Person", sub: "Add 1 team member", amount: 500, popular: false },
  { user_count: 8, persons: 3, label: "3 Extra Persons", sub: "Add 3 team members", amount: 1200, popular: true },
  { user_count: 10, persons: 5, label: "5 Extra Persons", sub: "Add 5 team members", amount: 1800, popular: false },
];

const ProjectInchargeTab = () => {
  const { user, token } = useAuthStore();
  const builderId = user?.builderId || user?.builder_id || user?.id;

  const [incharges, setIncharges] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [purchasedSlots, setPurchasedSlots] = useState(0);
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cashfree, setCashfree] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Modal form state
  const [form, setForm] = useState({ name: "", email: "", password: "", assigned_projects: [], permissions: ["Projects", "ERP"] });
  const [showPassword, setShowPassword] = useState(false);

  const totalLimit = FREE_LIMIT + purchasedSlots;
  const isLimitReached = incharges.length >= totalLimit;
  const remainingPersons = Math.max(0, totalLimit - incharges.length);
  const usagePercent = totalLimit > 0 ? Math.min(100, Math.round((incharges.length / totalLimit) * 100)) : 0;

  // Resolve paid packages from API pricing, with fallback to defaults
  const paidPlans = PERSON_PACKAGES.map((pkg) => {
    const fromApi = pricing.find((p) => p.user_count === pkg.user_count);
    return {
      ...pkg,
      amount: fromApi ? parseFloat(fromApi.amount) : pkg.amount,
    };
  });

  // Initialize Cashfree
  useEffect(() => {
    const initializeCashfree = async () => {
      try {
        const mode = process.env.REACT_APP_CASHFREE_MODE || "production";
        const cf = await load({ mode });
        setCashfree(cf);
      } catch (error) {
        console.error("Failed to load Cashfree SDK:", error);
      }
    };
    initializeCashfree();
  }, []);

  const fetchData = useCallback(async () => {
    if (!token || !builderId) return;
    setIsLoading(true);
    try {
      const [inchargeRes, projectsRes] = await Promise.all([
        axiosInstancev1.get("/builder/incharges", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axiosInstancev1.get(`/projects?builderId=${builderId}&status=active&limit=100&page=1`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (inchargeRes.data.success) {
        setIncharges(inchargeRes.data.incharges || []);
        setPricing(inchargeRes.data.pricing || []);
        setPurchasedSlots(inchargeRes.data.purchasedSlots || 0);
      }
      if (projectsRes.data.success) {
        setProjects(projectsRes.data.projects || []);
      }
    } catch (error) {
      console.error("Error fetching incharge data:", error);
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  }, [token, builderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openModal = (data = null) => {
    if (!data && isLimitReached) {
      setShowPlans(true);
      return toast.error("Team limit reached. Purchase extra persons to add more users.");
    }
    setEditData(data);
    if (data) {
      setForm({
        name: data.name || "",
        email: data.email || "",
        password: "",
        assigned_projects: data.assigned_projects || [],
        permissions: data.permissions || [],
      });
    } else {
      setForm({ name: "", email: "", password: "", assigned_projects: [], permissions: ["Projects", "ERP"] });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editData && isLimitReached) {
      setShowPlans(true);
      setShowModal(false);
      return toast.error("Team limit reached. Purchase extra persons to add more users.");
    }
    if (!form.email) return toast.error("Email is required");
    if (!editData && !form.password) return toast.error("Password is required");
    
    setSaving(true);
    try {
      if (editData) {
        const payload = { 
          name: form.name, 
          email: form.email, 
          assigned_projects: form.assigned_projects,
          permissions: form.permissions
        };
        if (form.password) payload.password = form.password;
        await axiosInstancev1.put(`/builder/incharges/${editData.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Incharge profile updated");
      } else {
        const payload = {
          ...form,
          assignedProjects: form.assigned_projects 
        };
        await axiosInstancev1.post("/builder/incharges", payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("New incharge created successfully");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save incharge");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to remove this incharge?")) return;
    try {
      await axiosInstancev1.delete(`/builder/incharges/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Incharge account removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove incharge");
    }
  };

  const handlePurchase = async (plan) => {
    const planToBuy = plan || selectedPlan;
    if (!planToBuy || !cashfree) return toast.error(planToBuy ? "Payment system is not ready" : "Please select a plan first");
    const persons = planToBuy.persons || (planToBuy.user_count === 6 ? 1 : planToBuy.user_count === 8 ? 3 : 5);

    setSaving(true);
    try {
      const { data } = await axiosInstancev1.post("/builder/incharges/create-order", {
        amount: planToBuy.amount,
        slotsToAdd: persons
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!data.success) throw new Error(data.message);

      cashfree.checkout({
        paymentSessionId: data.data.paymentSessionId,
        redirectTarget: "_modal",
      }).then(async (result) => {
        if (result.error) return toast.error(result.error.message);
        
        const verifyRes = await axiosInstancev1.post("/builder/incharges/verify-payment", {
          orderId: data.data.orderId
        }, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (verifyRes.data.success) {
          toast.success(`Added ${persons} person${persons > 1 ? "s" : ""} to your team capacity!`);
          setShowPlans(false);
          setSelectedPlan(null);
          fetchData();
        } else {
          toast.error("Payment verification failed");
        }
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Payment initiation failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab Header Section */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-white p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100 shrink-0">
            <ShieldCheck size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold text-gray-800">User Management</h2>
            <p className="text-xs text-gray-500 font-medium">
              {incharges.length} / {totalLimit} persons used
              {purchasedSlots > 0 ? ` · ${purchasedSlots} purchased` : ""}
              {remainingPersons === 0 ? " · Limit reached" : ` · ${remainingPersons} free`}
            </p>
            <div className="mt-2 h-1.5 w-full max-w-xs bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${usagePercent >= 100 ? "bg-rose-500" : usagePercent >= 80 ? "bg-amber-500" : "bg-blue-500"}`}
                style={{ width: `${usagePercent}%` }}
              />
            </div>
          </div>
        </div>
        <div className="flex gap-2">
           <button 
             onClick={() => { setShowPlans(!showPlans); if (!showPlans) setSelectedPlan(paidPlans.find(p => p.popular) || paidPlans[0]); }}
             className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition shadow-sm border ${showPlans ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
           >
             <CreditCard size={14} /> {isLimitReached ? "Add Persons" : "Upgrade"}
           </button>
           <button onClick={() => openModal()}
             className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 transition shadow-sm">
             <UserPlus size={16} /> Add Incharge
           </button>
        </div>
      </div>

      {/* Choose how many people to add */}
      {showPlans && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
          <div className="flex items-start justify-between gap-3 p-4 sm:p-5 border-b bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                <Users size={20} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800">Add Team Capacity</h3>
                <p className="text-xs text-gray-500 mt-0.5 font-medium">
                  Choose how many persons you want to add. One-time lifetime payment.
                </p>
              </div>
            </div>
            <button onClick={() => { setShowPlans(false); setSelectedPlan(null); }} className="text-gray-400 hover:text-gray-600 p-1"><X size={18} /></button>
          </div>

          <div className="p-4 sm:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {paidPlans.map((plan) => {
                const selected = selectedPlan?.user_count === plan.user_count;
                return (
                  <button
                    key={plan.user_count}
                    type="button"
                    onClick={() => setSelectedPlan(plan)}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all ${
                      selected
                        ? "border-blue-600 bg-blue-50/60 shadow-md ring-2 ring-blue-100"
                        : "border-gray-200 bg-white hover:border-blue-300 hover:bg-gray-50"
                    }`}
                  >
                    {plan.popular && (
                      <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[9px] font-bold uppercase tracking-wide whitespace-nowrap">
                        Best Value
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-3">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm ${
                        selected ? "bg-blue-600 text-white" : "bg-blue-50 text-blue-600"
                      }`}>
                        +{plan.persons}
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selected ? "border-blue-600 bg-blue-600 text-white" : "border-gray-300"
                      }`}>
                        {selected && <Check size={12} strokeWidth={3} />}
                      </div>
                    </div>
                    <p className="text-sm font-bold text-gray-800">{plan.label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{plan.sub}</p>
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-baseline justify-between">
                      <p className="text-lg font-bold text-blue-600">₹{parseFloat(plan.amount).toLocaleString("en-IN")}</p>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase">Lifetime</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100">
              <div className="text-sm text-gray-600 px-1">
                {selectedPlan ? (
                  <>
                    <span className="font-bold text-gray-800">Selected:</span>{" "}
                    {selectedPlan.label} — ₹{parseFloat(selectedPlan.amount).toLocaleString("en-IN")} lifetime
                  </>
                ) : (
                  <span className="text-gray-400 font-medium">Select a person package above to continue</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => handlePurchase(selectedPlan)}
                disabled={!selectedPlan || saving}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-bold hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm whitespace-nowrap"
              >
                <UserPlus size={16} />
                {saving ? "Processing..." : selectedPlan ? `Pay ₹${parseFloat(selectedPlan.amount).toLocaleString("en-IN")}` : "Select a Plan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Incharge List Table */}
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-100 border-t-blue-600"></div>
          <p className="text-sm font-medium text-slate-500">Retrieving team details...</p>
        </div>
      ) : incharges.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-slate-300 p-16 text-center flex flex-col items-center gap-4">
          <div className="w-20 h-20 rounded-full bg-slate-50 flex items-center justify-center text-slate-200 mb-2">
            <ShieldCheck size={40} />
          </div>
          <div>
            <p className="text-slate-800 font-bold text-lg">No Incharges Assigned</p>
            <p className="text-slate-500 text-sm max-w-xs mx-auto mt-1 font-medium">Create sub-accounts for your site supervisors to manage projects under your supervision.</p>
          </div>
          <button onClick={() => openModal()} className="mt-2 text-blue-600 font-bold text-sm hover:underline">Create first incharge</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-[10px] uppercase tracking-wider text-gray-500 font-bold">
                  <th className="text-left px-4 py-3">Team Member</th>
                  <th className="text-left px-4 py-3">Assigned Projects</th>
                  <th className="text-center px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {incharges.map((inc) => {
                  const initials = inc.name?.charAt(0).toUpperCase() || inc.email?.charAt(0).toUpperCase();
                  const assignedCount = (inc.assigned_projects || []).length;
                  return (
                    <tr key={inc.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-xs border border-gray-200 shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-800 text-sm truncate">{inc.name || "Unnamed User"}</p>
                            <p className="text-[10px] text-gray-500 font-medium truncate">{inc.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {assignedCount === 0 ? (
                            <span className="text-[10px] text-gray-400 italic">No projects assigned</span>
                          ) : (
                            inc.assigned_projects.slice(0, 2).map(pid => {
                              const proj = projects.find(p => p.id === pid);
                              return proj ? (
                                <span key={pid} className="px-2 py-0.5 rounded bg-blue-50 text-blue-600 text-[9px] font-bold border border-blue-100">
                                  {proj.project_name}
                                </span>
                              ) : null;
                            })
                          )}
                          {assignedCount > 2 && (
                            <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-500 text-[9px] font-bold border border-gray-200">
                              +{assignedCount - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => openModal(inc)} className="p-1.5 text-blue-600 bg-blue-50 hover:bg-blue-600 hover:text-white rounded-lg transition-colors flex items-center justify-center">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(inc.id)} className="p-1.5 text-red-600 bg-red-50 hover:bg-red-600 hover:text-white rounded-lg transition-colors flex items-center justify-center">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Incharge Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-gray-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b bg-gray-50/50">
              <h3 className="font-bold text-gray-800 text-sm">{editData ? "Edit Incharge Account" : "Register New Incharge"}</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            
            <form onSubmit={handleSave} className="p-4 space-y-4 max-h-[80vh] overflow-y-auto custom-scrollbar">
              {/* Account Info */}
              <div className="space-y-3">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Identity Details</p>
                <div>
                  <input required placeholder="Full Name *" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
                </div>
                <div>
                  <input type="email" required placeholder="Email Address *" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
                </div>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} placeholder={editData ? "Reset Password (Optional)" : "Security Password *"} value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} required={!editData} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-blue-500 transition-colors" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <Unlock size={14}/> : <Lock size={14}/>}
                  </button>
                </div>
              </div>

              {/* Project Assignments */}
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-1">Project Access</p>
                <div className="bg-gray-50 rounded-lg p-2 border border-gray-100 max-h-32 overflow-y-auto custom-scrollbar">
                   {projects.length === 0 ? (
                     <p className="text-xs text-gray-400 italic text-center py-2">No active projects available</p>
                   ) : (
                     <div className="grid grid-cols-1 gap-1">
                        {projects.map(pr => (
                          <label key={pr.id} className="flex items-center gap-2 p-1.5 bg-white rounded border border-gray-100 cursor-pointer hover:border-blue-200 transition-colors">
                            <input 
                              type="checkbox" 
                              checked={form.assigned_projects.includes(pr.id)} 
                              onChange={() => {
                                const active = form.assigned_projects.includes(pr.id);
                                setForm(f => ({
                                  ...f,
                                  assigned_projects: active ? f.assigned_projects.filter(id => id !== pr.id) : [...f.assigned_projects, pr.id]
                                }));
                              }}
                              className="w-3.5 h-3.5 accent-blue-600"
                            />
                            <span className="text-xs text-gray-700 font-medium truncate">{pr.project_name}</span>
                          </label>
                        ))}
                     </div>
                   )}
                </div>
              </div>

              <div className="flex gap-3 pt-4 bg-white sticky bottom-0">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-semibold hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 shadow-md shadow-blue-100">
                   {saving ? "Saving..." : editData ? "Update" : "Register"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
    </div>
  );
};

export default ProjectInchargeTab;
